// Orchestrates OAuth access tokens for the Apical integration.
//
// The actual OAuth 2.0 Authorization Code + PKCE flow against Clerk's hosted
// endpoints is performed by an injected `authClient` (see createApicalOAuth
// below, which wires oidc-client-ts). This class owns the app-level logic that
// is worth testing on its own: a per-organization token cache and silent ->
// interactive renewal. Tokens are cached in the browser only; nothing is
// persisted to the backend.

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

export class ApicalOAuth {
    constructor(authClient, { now = () => Date.now() } = {}) {
        this._client = authClient;
        this._now = now;
        this._cache = new Map(); // orgId -> { accessToken, expiresAt }
    }

    async getTokenForOrg(orgId) {
        const cached = this._cache.get(orgId);
        if (cached && cached.expiresAt > this._now()) {
            return cached.accessToken;
        }
        // Try silent renewal first (rides on the existing Clerk session); if the
        // session can't satisfy it (e.g. org not yet consented), fall back to an
        // interactive authorize/consent.
        let token;
        try {
            token = await this._client.acquireToken({ orgId, silent: true });
        } catch (e) {
            token = await this._client.acquireToken({ orgId, silent: false });
        }
        this._cache.set(orgId, { accessToken: token.accessToken, expiresAt: token.expiresAt });
        return token.accessToken;
    }

    clearOrg(orgId) {
        this._cache.delete(orgId);
    }

    clearAll() {
        this._cache.clear();
    }
}

export const APICAL_OAUTH_CALLBACK_PATH = '/apical-oauth-callback';

// Redirect URI used for both the popup sign-in and the silent-renew iframe.
function callbackUri() {
    return window.location.origin + APICAL_OAUTH_CALLBACK_PATH;
}

// True when the callback page is running inside the silent-renew hidden iframe
// (its parent is the app window) rather than the interactive sign-in popup (a
// top-level window whose parent is itself). The redirect must be handed to
// signinSilentCallback in the iframe and signinPopupCallback in the popup:
// calling the popup handler inside the iframe never resolves the silent
// request, so renewal times out and falls back to an interactive login.
export function isSilentCallbackContext(win = window) {
    return !!(win && win.parent && win.parent !== win);
}

// Wires oidc-client-ts against the (shared) Clerk instance's OAuth Application
// and returns the facade the Vue components consume. This is thin integration
// glue over the library; it requires a live Clerk OAuth app + browser to verify
// end-to-end (see docs/superpowers/specs). The token-cache/renewal logic it
// depends on lives in ApicalOAuth and is unit-tested independently.
//
// Required env:
//   VUE_APP_CLERK_OAUTH_AUTHORITY  - Clerk OAuth issuer / discovery base URL
//   VUE_APP_CLERK_OAUTH_CLIENT_ID  - the OAuth Application client id (Public + PKCE)
export function createApicalOAuth() {
    const userManager = new UserManager({
        authority: process.env.VUE_APP_CLERK_OAUTH_AUTHORITY,
        client_id: process.env.VUE_APP_CLERK_OAUTH_CLIENT_ID,
        redirect_uri: callbackUri(),
        silent_redirect_uri: callbackUri(),
        response_type: 'code', // Authorization Code + PKCE (public client, no secret)
        scope: 'openid profile email user:org:read',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    // Maps an oidc-client-ts User to the shape ApicalOAuth caches. expires_at is
    // epoch seconds; ApicalOAuth compares against Date.now() (ms). A 30s skew
    // buffer avoids handing out a token that dies mid-flight.
    const toToken = (user) => {
        return {
            accessToken: user.access_token,
            expiresAt: (user.expires_at - 30) * 1000,
        };
    };

    const authClient = {
        async acquireToken({ orgId, silent }) {
            // organization_id pins the token to the selected org so the issued
            // access token carries the org_id claim the backend relies on.
            // NOTE: exact param name for org pre-selection is pending Clerk
            // confirmation (see spec "items to verify").
            const args = { extraQueryParams: { organization_id: orgId } };
            const user = silent ? await userManager.signinSilent(args) : await userManager.signinPopup(args);
            return toToken(user);
        },
        login: () => userManager.signinPopup(),
        async logout() {
            await userManager.removeUser();
        },
        getUser: () => userManager.getUser(),
        async isSignedIn() {
            const user = await userManager.getUser();
            return !!user && !user.expired;
        },
        handlePopupCallback: () => userManager.signinPopupCallback(),
        handleSilentCallback: () => userManager.signinSilentCallback(),
    };

    const tokens = new ApicalOAuth(authClient);

    return {
        getTokenForOrg: (orgId) => tokens.getTokenForOrg(orgId),
        clearOrg: (orgId) => tokens.clearOrg(orgId),
        clearAll: () => tokens.clearAll(),
        signIn: () => authClient.login(),
        async signOut() {
            tokens.clearAll();
            await authClient.logout();
        },
        getUser: () => authClient.getUser(),
        isSignedIn: () => authClient.isSignedIn(),
        handlePopupCallback: () => authClient.handlePopupCallback(),
        handleSilentCallback: () => authClient.handleSilentCallback(),
    };
}

// Lazily-created app-wide singleton so every component and helper shares one
// per-org token cache. Created on first use (not at import) to avoid touching
// window/UserManager during module load.
let _instance = null;
export function apicalOAuth() {
    if (!_instance) {
        _instance = createApicalOAuth();
    }
    return _instance;
}
